import React, { useEffect, useRef, useState } from 'react';

import { ColumnConfig } from '@ant-design/plots';
import { Column } from '@ant-design/plots/lib';
import styled from 'styled-components';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import {
  ANTD_PLOT_CHART_COLUMN_WIDTH,
  COLUMN_CHART_OFFSET,
  DISTANCE_BETWEEN_GROUP_COLUMNS,
  MAX_BAR_WIDTH,
  MIN_BAR_WIDTH,
} from 'constants/charts';

import DataInsertionLegends from './DataInsertionLegends';
import { useDifferenceColorObserver } from './useDifferenceColorObserver';
import { formatYAxisValue, textStyle } from './utils';
import { shouldDisplayValueDifference } from '../valueDifferenceUtils';
import { DATA_RANGE_TYPE } from '../Widget/constants';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const ChartArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  z-index: 1;
`;

interface Props {
  columnProps: ColumnConfig;
  widgetType: number;
}

const ColumnChart = ({ columnProps, widgetType }: Props) => {
  const { elementRef, rangeType, filters } = useWidgetContext();
  const { community } = useCommunityViewContext();
  const chartRef = useRef<any>(null);
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const showValueDifference = shouldDisplayValueDifference(filters.comparisonTypeEnabled, filters.valueDisplay);
  const { onChartReady } = useDifferenceColorObserver(showValueDifference, widgetType);
  const [chartAreaHeight, setChartAreaHeight] = useState<number>(0);

  const [autoFit, setAutoFit] = useState(false);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  const widthPerColumn =
    (elementRef?.current?.offsetWidth ? elementRef.current.offsetWidth - COLUMN_CHART_OFFSET : 0) /
    columnProps.data.length;
  const [chartWidth, setChartWidth] = useState<number | undefined>(
    widthPerColumn < ANTD_PLOT_CHART_COLUMN_WIDTH ? columnProps.data.length * ANTD_PLOT_CHART_COLUMN_WIDTH : undefined
  );

  useEffect(() => {
    // If all columns can't fit inside the current width of the widget, we need to manually set the two fields in chart config:
    // - `autoFit` to false
    // - `width` to the desired width in pixels
    // To achieve this, an onResize listener is calculating if columns can fit, and set autoFit and width config fields accordingly.
    // Regular useEffect which triggers only once would not cover this properly, since responsive layout might trigger additional
    // renders and thus we need the on resize listener to handle it.
    if (elementRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
        const entry = entries[0];
        resizeTimeout.current = setTimeout(() => {
          const areaHeight = Math.max(160, Math.floor(chartAreaRef.current?.clientHeight ?? 0));
          setChartAreaHeight(areaHeight);
          const temporaryWidthPerColumn = (entry.contentRect.width - COLUMN_CHART_OFFSET) / columnProps.data.length;
          if (temporaryWidthPerColumn < ANTD_PLOT_CHART_COLUMN_WIDTH) {
            setAutoFit(false);
            setChartWidth(columnProps.data.length * ANTD_PLOT_CHART_COLUMN_WIDTH);
          } else {
            setAutoFit(true);
            setChartWidth(undefined);
          }
        }, 300);
      });

      resizeObserver.observe(elementRef.current);

      return () => {
        resizeObserver.disconnect();
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
      };
    }
  }, [elementRef, columnProps]);

  const uniqueGroups = [...new Set(columnProps.data.map((item) => item.group_by))];
  const showXAxisLabel = rangeType == DATA_RANGE_TYPE.GROUPED;

  // Extends Y-axis beyond data max to leave room for rotated difference labels above bars
  const DIFFERENCE_LABEL_Y_AXIS_PADDING = 1.2;
  const dataMax = showValueDifference ? Math.max(...columnProps.data.map((d: any) => Number(d.value) || 0)) : undefined;

  const onReady = (plot: any) => {
    onChartReady(plot);
  };
  const legendPadding = 24;

  const config: ColumnConfig = {
    ...columnProps,
    data: columnProps.data,
    isGroup: true,
    minColumnWidth: MIN_BAR_WIDTH,
    maxColumnWidth: MAX_BAR_WIDTH,
    dodgePadding: uniqueGroups.length > 1 ? DISTANCE_BETWEEN_GROUP_COLUMNS : undefined,
    appendPadding: showValueDifference ? [60, 10, legendPadding, 10] : [40, 10, legendPadding, 10],
    legend: {
      position: 'bottom',
      layout: 'horizontal',
      marker: { symbol: 'circle', style: { r: 0, fill: 'transparent', stroke: 'transparent' } },
      flipPage: false,
      itemName: {
        formatter: (title: string) => '',
        style: (item: any) => textStyle(item.name, columnProps.data),
      },
    },
    xAxis: showXAxisLabel
      ? columnProps.xAxis
      : {
          ...columnProps.xAxis,
          label: null,
        },
    yAxis: {
      ...columnProps.yAxis,
      min: 0,
      ...(dataMax != null ? { max: Math.ceil(dataMax * DIFFERENCE_LABEL_Y_AXIS_PADDING) } : {}),
      label: {
        formatter: (text: string) => formatYAxisValue(text, widgetType, community),
      },
    },
    autoFit,
    width: chartWidth,
    height: chartAreaHeight > 0 ? chartAreaHeight : undefined,
  };

  return (
    <Container>
      <ChartArea ref={chartAreaRef}>
        <Column
          key={`${chartWidth ?? 'auto'}-${chartAreaHeight}-${columnProps.data.length}-${rangeType}-${showValueDifference ? 1 : 0}`}
          chartRef={chartRef}
          onReady={onReady}
          {...config}
        />
      </ChartArea>
      <DataInsertionLegends data={columnProps.data} widgetType={widgetType} />
    </Container>
  );
};

export default ColumnChart;
